import React from 'react'
import PropTypes from 'prop-types'
import { Controller, useForm } from 'react-hook-form'
import { Box, TextField, Typography } from '@material-ui/core'

import { BaseDialog } from 'components'

import helpers from 'helpers'

import useSnackbar from 'hooks/useSnackbar'

import constants from 'constants/index'
import * as service from 'service'
import useStyles from './styles'
import schema from './schema'
import theme from 'theme'

const CompleteModal = ({
  open,
  setOpen,
  setLoading,
  activity = {},
  answer,
  governanceProjectId,
  reject = false,
  refresh,
}) => {
  const snackbar = useSnackbar()
  const classes = useStyles()

  const { control, errors, handleSubmit } = useForm({
    validationSchema: schema({ reject }),
    defaultValues: {
      rejectionReason: '',
      approvalComment: '',
    },
  })

  const {
    AWAITING_APPROVAL_STATUS_ID,
    COMPLETED_STATUS_ID,
    REJECTED_STATUS_ID,
  } = constants.governanceProjectActivity

  const isAwaitingApproval = activity?.status === AWAITING_APPROVAL_STATUS_ID

  const handleClose = () => setOpen(false)

  const onSubmit = async (data) => {
    try {
      setLoading(true)

      if (!answer) {
        snackbar.open({
          message:
            'É necessário responder a pergunta antes de finalizar a atividade.',
          variant: 'error',
        })
        return
      }

      await service.dponet.governanceProjectActivity.changeStatus({
        governanceProjectId,
        id: activity?.id,
        status: reject
          ? REJECTED_STATUS_ID
          : isAwaitingApproval
          ? COMPLETED_STATUS_ID
          : AWAITING_APPROVAL_STATUS_ID,
        rejectionReason: data?.rejectionReason,
        approvalComment: data?.approvalComment,
      })

      snackbar.open({
        message: `Atividade ${
          reject
            ? 'rejeitada'
            : isAwaitingApproval
            ? 'finalizada'
            : 'enviada para aprovação'
        } com sucesso!`,
        variant: 'success',
      })

      refresh()
      handleClose()
    } catch (error) {
      console.error(error)
      snackbar.open({
        message: helpers.formatters.errorMessage(error?.response?.data?.error),
        variant: 'error',
      })
    } finally {
      setLoading(false)
    }
  }

  return (
    <BaseDialog
      open={open}
      setOpen={setOpen}
      title={
        reject
          ? 'Reprovar atividade'
          : isAwaitingApproval
          ? 'Aprovar atividade'
          : 'Enviar para aprovação'
      }
      closeButtonText="Cancelar"
      actionButtonText={
        reject ? 'Reprovar' : isAwaitingApproval ? 'Aprovar' : 'Enviar'
      }
      formRef="activity-complete-modal-form"
    >
      <Box
        py={2}
        component="form"
        onSubmit={handleSubmit(onSubmit)}
        id="activity-complete-modal-form"
      >
        <Box display="flex" flexDirection="column" gridGap={theme.spacing(2)}>
          {reject ? (
            <>
              <Typography align="justify">
                Tem certeza que deseja <strong>reprovar esta atividade?</strong>{' '}
                Ao reprovar, ela será{' '}
                <strong>retornada aos responsáveis</strong> para que verifiquem
                o que deve ser corrigido.
              </Typography>
              <Controller
                control={control}
                name="rejectionReason"
                as={
                  <TextField
                    label="Motivo da rejeição"
                    variant="outlined"
                    placeholder="Informe o motivo da rejeição para facilitar o ajuste das pendências."
                    error={!!errors?.rejectionReason}
                    helperText={errors?.rejectionReason?.message}
                    multiline
                    fullWidth
                    minRows={6}
                    maxRows={16}
                  />
                }
              />
            </>
          ) : isAwaitingApproval ? (
            <>
              <Typography align="justify">
                Tem certeza que deseja <strong>aprovar esta atividade?</strong>{' '}
                Ao aprovar, ela será <strong>finalizada</strong>, mas poderá ser
                reaberta posteriormente se necessário.
              </Typography>
              <Controller
                control={control}
                name="approvalComment"
                as={
                  <TextField
                    label="Comentário (opcional)"
                    variant="outlined"
                    multiline
                    fullWidth
                    minRows={6}
                    maxRows={16}
                  />
                }
              />
            </>
          ) : (
            <>
              <Typography align="justify" gutterBottom>
                Tem certeza que deseja&nbsp;
                <strong>enviar esta atividade para aprovação?</strong> Antes de
                confirmar, revise todos os dados da atividade para evitar
                possíveis reprovações e garantir que todas as informações
                estejam corretas.
              </Typography>
              <Typography align="justify">
                Certifique-se de que:
                <ul className={classes.listContainer}>
                  <li>Todas as evidências foram devidamente respondidas;</li>
                  <li>Todos os planos de ação pendentes estão concluídos;</li>
                  <li>
                    Cada evidência possui ao menos um documento vinculado.
                  </li>
                </ul>
              </Typography>
            </>
          )}
        </Box>
      </Box>
    </BaseDialog>
  )
}

CompleteModal.propTypes = {
  open: PropTypes.bool.isRequired,
  setOpen: PropTypes.func.isRequired,
  setLoading: PropTypes.func.isRequired,
  activity: PropTypes.object,
  answer: PropTypes.string,
  governanceProjectId: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    .isRequired,
  refresh: PropTypes.func.isRequired,
}

export default CompleteModal
